import { createApp, configureCompat } from 'vue';

// Vendor imports
import Buefy from 'buefy';

// Import Buefy services to initialize them
import ModalService from '/@/modules/core/ui/services/ModalService';
import ToastService from '/@/modules/core/ui/services/ToastService';
import DialogService from '/@/modules/core/ui/services/DialogService';
import SnackbarService from '/@/modules/core/ui/services/SnackbarService';
import NotificationService from '/@/modules/core/ui/services/NotificationService';

// Styles
import '@/styles.ts';

// Bootstrap the app
import router from '/@/router';
import { Env } from '/@/Env';
import Lang from '/@/modules/core/app/services/Lang';
import App from '/@/App.vue';
import '/@/bootstrap';

// Custom imports
import { StaticOptionPlugin } from '/@/modules/core/app/plugins/StaticOptionPlugin';
import { LocalOptionPlugin } from '/@/modules/core/app/plugins/LocalOptionPlugin';
import { GlobalInjectionPlugin } from '/@/modules/core/app/plugins/GlobalInjectionPlugin';
import { EventBusPlugin } from '/@/modules/core/app/plugins/EventBusOptionPlugin';
import DocumentUIService from '/@/modules/core/app/services/DocumentUIService';
import EventBus from '/@/modules/core/app/helpers/EventBus';
import GoogleTagManagerService from '/@/integrations/googletagmanager/GoogleTagManagerService';
import HubSpotScriptService from '/@/integrations/hubspot/HubSpotScriptService';
import VueDOMPurifyHTML from 'vue-dompurify-html';

// Extension imports
import '/@/modules/core/form/validations';
import '/@/modules/core/form/models/File';
import SentryService from '/@/integrations/sentry/SentryService';
import LogService from '/@/integrations/betterstack/LogService';
import { GuidanceStepStage } from '/@/modules/guidancestep/enums/GuidanceStepStage';

import '@/dayjs.config';
import ButtonView from '/@/modules/core/ui/components/wrappers/ButtonView.vue';
import SkeletonView from '/@/modules/core/ui/components/wrappers/SkeletonView.vue';
import TooltipView from '/@/modules/core/ui/components/wrappers/TooltipView.vue';
// should be a wrapper in future
import ToolsNavItem from '@/modules/admin/components/resource/ToolsNavItem.vue';
import IconView from '/@/modules/core/ui/components/wrappers/IconView.vue';
import ApiService from '@/modules/core/api/services/ApiService';
import AuthEnvironmentValuesService from '/@/modules/auth/services/AuthEnvironmentValuesService';
import ServiceWorkerService from '@/integrations/serviceworker/ServiceWorkerService';
import StoreHelper from '@/modules/core/app/helpers/StoreHelper';

// Configure compatibility mode for gradual migration
configureCompat({
  RENDER_FUNCTION: false,
  COMPONENT_ASYNC: false,
  COMPONENT_V_MODEL: false,
  MODE: 2 // Enable Vue 2 compatibility mode
});

// Listens to browser back button
DocumentUIService.listenToPopState();

// Create the Vue app instance
const app = createApp(App);

// Create and use Pinia
app.use(StoreHelper.init());

// Use Buefy
app.use(Buefy, {
  defaultIconPack: 'fa'
});

// Register global components
// temporary until we refactor these out of functional components
app.component(ButtonView.name, ButtonView);
app.component(SkeletonView.name, SkeletonView);
app.component(TooltipView.name, TooltipView);
app.component(IconView.name, IconView);
app.component(ToolsNavItem.name, ToolsNavItem);

// Use plugins
app.use(StaticOptionPlugin);
app.use(LocalOptionPlugin);
app.use(GlobalInjectionPlugin);
app.use(EventBusPlugin);
app.use(VueDOMPurifyHTML, {
  default: {
    ADD_ATTR: ['target', 'rel']
  }
});

// Global addons
if (Env.isDevelopment()) {
  window.$router = router;
  window.Env = Env;
  if (Env.nodeEnv().VITE_DISABLE_FULL_PAGE_RELOADING) {
    import('@/modules/dev/skip-hmr.js');
  }
}
window.EventBus = EventBus;
window.GuidanceStepStage = GuidanceStepStage;

(async () => {
  await AuthEnvironmentValuesService.fetchEnvironmentValues();

  // Use router and i18n
  app.use(router);
  app.use(Lang.i18n);

  // Initialize Buefy programmatic services with the app instance
  ModalService.setApp(app);
  ToastService.setApp(app);
  DialogService.setApp(app);
  SnackbarService.setApp(app);
  NotificationService.setApp(app);

  // Dev-only component name stamping; dynamic import keeps the module out of the production
  // runtime, and it must be awaited before mount so the initial tree gets the mixin
  if (Env.isDevelopment()) {
    const { ComponentNamePlugin } = await import('/@/modules/core/app/plugins/ComponentNamePlugin');
    app.use(ComponentNamePlugin);
  }

  // Mount the app
  app.mount('#app');

  // Set production tip (now on app.config)
  app.config.productionTip = false;

  if (
    Env.nodeEnv().VITE_APP_SHOW_MAINTENANCE_PAGE &&
    !localStorage.getItem('can_skip_maintenance_page')
  ) {
    document.body.innerHTML = `<iframe style="position: fixed; width: 100vw; height: 100vh; top: 0;" src="https://maintenance.clearestate.com?v=${new Date().toISOString()}"></iframe>`;
    setTimeout(() => {
      ApiService.cancelPendingGetRequests();
    }, 1000);
    return;
  }

  SentryService.init(app);
  GoogleTagManagerService.init();
  HubSpotScriptService.init();
  LogService.init();
  ServiceWorkerService.init();
})();
